/**
 * `SettingsLanding` — GVW-145 settings landing.
 *
 * Token-only navigation list of all GeoViewer settings sub-pages per
 * brief §2 / §6 / §8. Renders the consumed-shell main panel; left rail
 * styling is the workspace shell's responsibility.
 *
 * Persistence: n/a (index page).
 *
 * Cite: design/30-engineering/ui-implementation/GVW-145/brief.md
 */

import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

interface SettingsSection {
  id: string;
  label: string;
  icon: string;
  route: string;
  description: string;
  scope: 'app' | 'org';
}

const SETTINGS_SECTIONS: SettingsSection[] = [
  { id: 'share-link', label: 'Share-link defaults', icon: 'share', route: '/workspace/settings/share-link', description: 'Default share tier, audit, recipient allowlist', scope: 'app' },
  { id: 'credentials', label: 'Layer credentials', icon: 'key', route: '/workspace/settings/credentials', description: 'Per-source connector health and rotation', scope: 'app' },
  { id: 'integrations', label: 'Integrations', icon: 'extension', route: '/workspace/settings/integrations', description: 'Read-only mirror of platform Admin', scope: 'org' },
  { id: 'crs', label: 'Default CRS / units', icon: 'language', route: '/workspace/settings/crs', description: 'Coordinate reference system and units', scope: 'app' },
  { id: 'accessibility', label: 'Accessibility', icon: 'accessibility_new', route: '/workspace/settings/accessibility', description: 'Density, motion, contrast', scope: 'app' },
  { id: '3d-canvas', label: '3D canvas tone', icon: 'dark_mode', route: '/workspace/settings/3d-canvas', description: 'Light / neutral-dark per session', scope: 'app' },
  { id: 'telemetry', label: 'Telemetry', icon: 'monitoring', route: '/workspace/settings/telemetry', description: 'Diagnostic data opt-in', scope: 'app' },
  { id: 'notifications', label: 'Notifications', icon: 'notifications', route: '/workspace/settings/notifications', description: 'Per-category notification preferences', scope: 'app' },
];

export function SettingsLanding() {
  const liveRegion = useLiveRegion();

  useEffect(() => {
    document.title = 'Settings · GeoViewer';
    liveRegion.announce('status', 'Settings page loaded.');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-145-heading"
      data-gvw-id="GVW-145"
      data-route="/workspace/settings"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <header style={{ marginBottom: tokens.space[5] }}>
        <h1
          id="gvw-145-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[3],
          }}
        >
          <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
            settings
          </span>
          Settings
        </h1>
        <p
          style={{
            marginTop: tokens.space[2],
            marginBottom: 0,
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.md,
          }}
        >
          GeoViewer-scoped settings. Org-wide settings live in the platform Admin app.
        </p>
      </header>

      <nav
        aria-label="Settings sections"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${tokens.layout.panelMd}px, 1fr))`,
          gap: tokens.space[3],
        }}
      >
        {SETTINGS_SECTIONS.map((section) => (
          <NavLink
            key={section.id}
            to={section.route}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: tokens.space[3],
              padding: tokens.space[4],
              borderRadius: tokens.radius.md,
              border: tokens.border.thin,
              background: isActive ? tokens.color.surface[2] : tokens.color.surface[1],
              color: tokens.color.ink.primary,
              textDecoration: 'none',
              minHeight: tokens.density.touchMin,
            })}
          >
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Material Symbols Outlined',
                fontSize: 24,
                color: tokens.color.ink.secondary,
                flexShrink: 0,
              }}
            >
              {section.icon}
            </span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: tokens.font.size.lg,
                  fontWeight: tokens.font.weight.medium,
                  marginBottom: tokens.space[1],
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.space[2],
                }}
              >
                {section.label}
                {section.scope === 'org' ? (
                  <span
                    style={{
                      fontSize: tokens.font.size.xs,
                      padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                      background: tokens.color.surface[3],
                      color: tokens.color.ink.secondary,
                      borderRadius: tokens.radius.chip,
                      fontWeight: tokens.font.weight.regular,
                    }}
                  >
                    Org-wide
                  </span>
                ) : null}
              </div>
              <div
                style={{
                  fontSize: tokens.font.size.sm,
                  color: tokens.color.ink.secondary,
                  lineHeight: tokens.font.lineHeight.normal,
                }}
              >
                {section.description}
              </div>
            </div>
          </NavLink>
        ))}
      </nav>
    </main>
  );
}
